import { useEffect, useState } from "react";

import { parseDetail } from "../utils/errors";
import CopyableGuid from "../components/CopyableGuid";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import useRequests from "../hooks/http";

export default function OrganisationDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { get, post, patch, del } = useRequests();

  const [org, setOrg] = useState<Organisation | null>(null);
  const [members, setMembers] = useState<OrganisationMember[]>([]);
  const [credentialUsers, setCredentialUsers] = useState<
    { id: number; email: string }[]
  >([]);
  const [tenantRoles, setTenantRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [memberError, setMemberError] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", slug: "" });
  const [editError, setEditError] = useState("");

  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [newMember, setNewMember] = useState({
    user_id: "" as number | "",
    role_id: "" as number | "",
  });
  const [addMemberError, setAddMemberError] = useState("");

  const [editRoleOpen, setEditRoleOpen] = useState(false);
  const [editRoleMember, setEditRoleMember] =
    useState<OrganisationMember | null>(null);
  const [editRoleValue, setEditRoleValue] = useState<number | "">("");
  const [editRoleError, setEditRoleError] = useState("");

  const [enabledApps, setEnabledApps] = useState<
    { id: number; application_id: number }[]
  >([]);
  const [tenantApps, setTenantApps] = useState<Application[]>([]);
  const [addAppOpen, setAddAppOpen] = useState(false);
  const [newAppId, setNewAppId] = useState<number | "">("");
  const [appError, setAppError] = useState("");

  const [orgRoles, setOrgRoles] = useState<OrganisationRole[]>([]);
  const [addOrgRoleOpen, setAddOrgRoleOpen] = useState(false);
  const [newOrgRoleId, setNewOrgRoleId] = useState<number | "">("");
  const [orgRoleError, setOrgRoleError] = useState("");

  const [deactivating, setDeactivating] = useState(false);
  const [infoToast, setInfoToast] = useState("");

  useEffect(() => {
    Promise.all([
      get(`/admin/organisations/${id}`),
      get(`/admin/organisations/${id}/members`),
      get(`/admin/organisations/${id}/roles`),
    ]).then(([orgRes, membersRes, rolesRes]) => {
      if (orgRes.status === 200) {
        setOrg(orgRes.data);
        const tenantId = orgRes.data.tenant_id;
        get(`/admin/tenants/${tenantId}/credential-users`).then(
          ({ data, status }) => {
            if (status === 200 && Array.isArray(data)) setCredentialUsers(data);
          },
        );
        get(`/admin/tenants/${tenantId}/applications`).then(
          ({ data, status }) => {
            if (status === 200 && Array.isArray(data)) setTenantApps(data);
          },
        );
        get(`/admin/tenants/${tenantId}/roles`).then(({ data, status }) => {
          if (status === 200 && Array.isArray(data)) setTenantRoles(data);
        });
      }
      get(`/admin/organisations/${id}/applications`).then(
        ({ data, status }) => {
          if (status === 200 && Array.isArray(data)) setEnabledApps(data);
        },
      );
      if (membersRes.status === 200 && Array.isArray(membersRes.data))
        setMembers(membersRes.data);
      if (rolesRes.status === 200 && Array.isArray(rolesRes.data))
        setOrgRoles(rolesRes.data);
      setLoading(false);
    });
  }, [id, get]);

  const handleEnableApp = async () => {
    setAppError("");
    if (newAppId === "") return;
    const { data, status } = await post(
      `/admin/organisations/${id}/applications`,
      null,
      {
        application_id: newAppId,
      },
    );
    if (status === 201) {
      setEnabledApps((prev) => [...prev, data]);
      setAddAppOpen(false);
      setNewAppId("");
    } else {
      setAppError(
        parseDetail(
          data?.detail,
          t("organisations.detail.errors.enableAppFailed"),
        ),
      );
    }
  };

  const handleDisableApp = async (enablementId: number) => {
    setAppError("");
    const { data, status } = await del(
      `/admin/organisations/${id}/applications/${enablementId}`,
    );
    if (status === 200) {
      setEnabledApps((prev) => prev.filter((e) => e.id !== enablementId));
    } else {
      setAppError(
        parseDetail(
          data?.detail,
          t("organisations.detail.errors.disableAppFailed"),
        ),
      );
    }
  };

  const handleOpenEdit = () => {
    if (!org) return;
    setEditForm({ name: org.name, slug: org.slug });
    setEditError("");
    setEditOpen(true);
  };

  const handleEdit = async () => {
    setEditError("");
    const { data, status } = await patch(
      `/admin/organisations/${id}`,
      editForm,
    );
    if (status === 200) {
      setOrg(data);
      setEditOpen(false);
    } else {
      setEditError(
        parseDetail(data?.detail, t("organisations.detail.errors.editFailed")),
      );
    }
  };

  const handleDeactivate = async () => {
    setDeactivating(true);
    const { data, status } = await del(`/admin/organisations/${id}`);
    if (status === 200) {
      setOrg(data);
    }
    setDeactivating(false);
  };

  const handleAddMember = async () => {
    setAddMemberError("");
    const selectedUser = credentialUsers.find(
      (u) => u.id === newMember.user_id,
    );
    if (!selectedUser) return;

    const { data, status } = await post(
      `/admin/organisations/${id}/members`,
      null,
      {
        email: selectedUser.email,
        role_id: newMember.role_id === "" ? null : newMember.role_id,
      },
    );
    if (status === 201 || status === 200) {
      setMembers((prev) => [...prev, data]);
      setAddMemberOpen(false);
      setNewMember({ user_id: "" as number | "", role_id: "" as number | "" });
    } else {
      setAddMemberError(
        parseDetail(
          data?.detail,
          t("organisations.detail.errors.addMemberFailed"),
        ),
      );
    }
  };

  const handleRemoveMember = async (memberId: number) => {
    setMemberError("");
    const { data, status } = await del(
      `/admin/organisations/${id}/members/${memberId}`,
    );
    if (status === 200) {
      setMembers((prev) =>
        prev.map((m) => (m.id === memberId ? { ...m, active: false } : m)),
      );
    } else {
      setMemberError(
        parseDetail(
          data?.detail,
          t("organisations.detail.errors.removeMemberFailed"),
        ),
      );
    }
  };

  const handleOpenEditRole = (member: OrganisationMember) => {
    setEditRoleMember(member);
    setEditRoleValue(member.role_id ?? "");
    setEditRoleError("");
    setEditRoleOpen(true);
  };

  const handleSaveRole = async () => {
    if (!editRoleMember) return;
    setEditRoleError("");
    const { data, status } = await patch(
      `/admin/organisations/${id}/members/${editRoleMember.id}`,
      { role_id: editRoleValue === "" ? null : editRoleValue },
    );
    if (status === 200) {
      setMembers((prev) =>
        prev.map((m) =>
          m.id === editRoleMember.id
            ? {
                ...m,
                role_id:
                  editRoleValue === "" ? null : (editRoleValue as number),
              }
            : m,
        ),
      );
      setEditRoleOpen(false);
      setEditRoleMember(null);
    } else {
      setEditRoleError(
        parseDetail(
          data?.detail,
          t("organisations.detail.errors.updateRoleFailed"),
        ),
      );
    }
  };

  const handleAssignOrgRole = async () => {
    setOrgRoleError("");
    if (newOrgRoleId === "") return;
    const { data, status } = await post(
      `/admin/organisations/${id}/roles`,
      null,
      {
        role_id: newOrgRoleId,
      },
    );
    if (status === 201) {
      setOrgRoles((prev) => [...prev, data]);
      setAddOrgRoleOpen(false);
      setNewOrgRoleId("");
    } else {
      setOrgRoleError(
        parseDetail(
          data?.detail,
          t("organisations.detail.errors.assignOrgRoleFailed"),
        ),
      );
    }
  };

  const handleUnassignOrgRole = async (assignmentId: number) => {
    setOrgRoleError("");
    const { data, status } = await del(
      `/admin/organisations/${id}/roles/${assignmentId}`,
    );
    if (status === 200) {
      setOrgRoles((prev) => prev.filter((r) => r.id !== assignmentId));
    } else {
      setOrgRoleError(
        parseDetail(
          data?.detail,
          t("organisations.detail.errors.unassignOrgRoleFailed"),
        ),
      );
    }
  };

  const roleNameForId = (roleId: number | null) => {
    if (!roleId) return null;
    return tenantRoles.find((r) => r.id === roleId)?.name ?? null;
  };

  if (loading) return <LinearProgress />;
  if (!org)
    return <Alert severity="error">{t("organisations.detail.notFound")}</Alert>;

  const unassignedRoles = tenantRoles.filter(
    (r) => !orgRoles.some((or) => or.role_id === r.id),
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4">{org.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {org.slug}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t("organisations.detail.description")}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip
            label={org.active ? t("common.active") : t("common.inactive")}
            color={org.active ? "success" : "warning"}
          />
          <Button variant="outlined" size="small" onClick={handleOpenEdit}>
            {t("common.edit")}
          </Button>
          {org.active && (
            <Button
              variant="outlined"
              size="small"
              color="warning"
              disabled={deactivating}
              onClick={handleDeactivate}
            >
              {t("common.deactivate")}
            </Button>
          )}
        </Box>
      </Box>

      <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
        <CopyableGuid label={t("common.guid")} value={org.guid} />
        <Typography variant="body2" color="text.secondary">
          {t("common.created")}: {new Date(org.created).toLocaleString()}
        </Typography>
      </Card>

      {/* Org-level Roles */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="h5">
          {t("organisations.detail.sections.roles")} ({orgRoles.length})
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            if (unassignedRoles.length === 0) {
              setInfoToast(t("organisations.detail.noRolesAvailable"));
              return;
            }
            setOrgRoleError("");
            setAddOrgRoleOpen(true);
          }}
        >
          {t("organisations.detail.assignOrgRole")}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("organisations.detail.sections.rolesDescription")}
      </Typography>

      {orgRoleError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setOrgRoleError("")}
        >
          {orgRoleError}
        </Alert>
      )}

      {orgRoles.length === 0 ? (
        <Typography color="text.secondary" sx={{ p: 2 }}>
          {t("organisations.detail.emptyOrgRoles")}
        </Typography>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("organisations.detail.columns.role")}</TableCell>
                <TableCell>
                  {t("organisations.detail.columns.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orgRoles.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <Link to="/app/roles" style={{ textDecoration: "none" }}>
                      <Chip
                        size="small"
                        label={roleNameForId(r.role_id) ?? `Role ${r.role_id}`}
                        variant="outlined"
                        clickable
                      />
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleUnassignOrgRole(r.id)}
                    >
                      {t("common.remove")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 4,
          mb: 1,
        }}
      >
        <Typography variant="h5">
          {t("organisations.detail.sections.members")} ({members.length})
        </Typography>
        <Button variant="contained" onClick={() => setAddMemberOpen(true)}>
          {t("organisations.detail.addMember")}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("organisations.detail.sections.membersDescription")}
      </Typography>

      {memberError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setMemberError("")}
        >
          {memberError}
        </Alert>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("organisations.detail.columns.email")}</TableCell>
              <TableCell>{t("organisations.detail.columns.orgRole")}</TableCell>
              <TableCell>{t("organisations.detail.columns.status")}</TableCell>
              <TableCell>{t("organisations.detail.columns.created")}</TableCell>
              <TableCell>{t("organisations.detail.columns.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.map((member) => {
              const roleName = roleNameForId(member.role_id);
              return (
                <TableRow key={member.id}>
                  <TableCell>
                    {member.email ?? `User ${member.user_id}`}
                  </TableCell>
                  <TableCell>
                    {roleName ? (
                      <Chip size="small" label={roleName} variant="outlined" />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t("organisations.detail.noRole")}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={
                        member.active ? t("common.active") : t("common.removed")
                      }
                      color={member.active ? "success" : "error"}
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(member.created).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {member.active && (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleOpenEditRole(member)}
                          >
                            {t("organisations.detail.assignRole")}
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleRemoveMember(member.id)}
                          >
                            {t("common.remove")}
                          </Button>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {members.length === 0 && (
        <Typography color="text.secondary" sx={{ p: 2 }}>
          {t("organisations.detail.emptyMembers")}
        </Typography>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 4,
          mb: 1,
        }}
      >
        <Typography variant="h5">
          {t("organisations.detail.sections.applications")} (
          {enabledApps.length})
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            const available = tenantApps.filter(
              (a) => !enabledApps.some((e) => e.application_id === a.id),
            );
            if (available.length === 0) {
              setInfoToast(t("organisations.detail.noApplicationsAvailable"));
              return;
            }
            setAddAppOpen(true);
            setAppError("");
          }}
        >
          {t("organisations.detail.enableApplication")}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("organisations.detail.sections.applicationsDescription")}
      </Typography>

      {appError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAppError("")}>
          {appError}
        </Alert>
      )}

      {enabledApps.length === 0 ? (
        <Typography color="text.secondary" sx={{ p: 2 }}>
          {t("organisations.detail.emptyApplications")}
        </Typography>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  {t("organisations.detail.columns.application")}
                </TableCell>
                <TableCell>
                  {t("organisations.detail.columns.identifier")}
                </TableCell>
                <TableCell>
                  {t("organisations.detail.columns.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {enabledApps.map((e) => {
                const app = tenantApps.find((a) => a.id === e.application_id);
                return (
                  <TableRow key={e.id}>
                    <TableCell>
                      {app?.name ?? `App ${e.application_id}`}
                    </TableCell>
                    <TableCell>{app?.identifier ?? "—"}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleDisableApp(e.id)}
                      >
                        {t("common.disable")}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Assign Org Role dialog */}
      <Dialog
        open={addOrgRoleOpen}
        onClose={() => setAddOrgRoleOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("organisations.detail.dialogs.assignOrgRole.title")}
        </DialogTitle>
        <DialogContent>
          {orgRoleError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {orgRoleError}
            </Alert>
          )}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, mt: 1 }}
          >
            {t("organisations.detail.dialogs.assignOrgRole.description")}
          </Typography>
          <FormControl fullWidth margin="normal">
            <InputLabel>{t("organisations.detail.columns.role")}</InputLabel>
            <Select
              value={newOrgRoleId}
              label={t("organisations.detail.columns.role")}
              onChange={(e) =>
                setNewOrgRoleId(
                  e.target.value === "" ? "" : Number(e.target.value),
                )
              }
            >
              {unassignedRoles.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOrgRoleOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleAssignOrgRole}
            disabled={newOrgRoleId === ""}
          >
            {t("organisations.detail.dialogs.assignOrgRole.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enable Application dialog */}
      <Dialog
        open={addAppOpen}
        onClose={() => setAddAppOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("organisations.detail.dialogs.enableApp.title")}
        </DialogTitle>
        <DialogContent>
          {appError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {appError}
            </Alert>
          )}
          <FormControl fullWidth margin="normal">
            <InputLabel>
              {t("organisations.detail.columns.application")}
            </InputLabel>
            <Select
              value={newAppId}
              label={t("organisations.detail.columns.application")}
              onChange={(e) =>
                setNewAppId(e.target.value === "" ? "" : Number(e.target.value))
              }
            >
              {tenantApps
                .filter(
                  (a) => !enabledApps.some((e) => e.application_id === a.id),
                )
                .map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddAppOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleEnableApp}
            disabled={newAppId === ""}
          >
            {t("organisations.detail.dialogs.enableApp.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Organisation dialog */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("organisations.detail.dialogs.edit.title")}
        </DialogTitle>
        <DialogContent>
          {editError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {editError}
            </Alert>
          )}
          <TextField
            label={t("common.name")}
            fullWidth
            margin="normal"
            value={editForm.name}
            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
          />
          <TextField
            label={t("common.slug")}
            fullWidth
            margin="normal"
            value={editForm.slug}
            onChange={(e) => setEditForm({ ...editForm, slug: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleEdit}
            disabled={!editForm.name || !editForm.slug}
          >
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Member dialog */}
      <Dialog
        open={addMemberOpen}
        onClose={() => setAddMemberOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("organisations.detail.dialogs.addMember.title")}
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, mt: 1 }}
          >
            {t("organisations.detail.dialogs.addMember.description")}
          </Typography>
          {addMemberError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {addMemberError}
            </Alert>
          )}
          <FormControl fullWidth margin="normal">
            <InputLabel>
              {t("organisations.detail.dialogs.addMember.userLabel")}
            </InputLabel>
            <Select
              value={newMember.user_id}
              label={t("organisations.detail.dialogs.addMember.userLabel")}
              onChange={(e) =>
                setNewMember({
                  ...newMember,
                  user_id: e.target.value === "" ? "" : Number(e.target.value),
                })
              }
            >
              {credentialUsers.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>
              {t("organisations.detail.dialogs.addMember.roleLabel")}
            </InputLabel>
            <Select
              value={newMember.role_id}
              label={t("organisations.detail.dialogs.addMember.roleLabel")}
              onChange={(e) =>
                setNewMember({
                  ...newMember,
                  role_id: e.target.value === "" ? "" : Number(e.target.value),
                })
              }
            >
              <MenuItem value="">
                <em>{t("organisations.detail.noRole")}</em>
              </MenuItem>
              {tenantRoles.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t("organisations.detail.dialogs.addMember.roleHelp")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMemberOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleAddMember}
            disabled={newMember.user_id === ""}
          >
            {t("organisations.detail.dialogs.addMember.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!infoToast}
        autoHideDuration={3000}
        onClose={() => setInfoToast("")}
        message={infoToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      {/* Assign Member Role dialog */}
      <Dialog
        open={editRoleOpen}
        onClose={() => setEditRoleOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("organisations.detail.dialogs.assignRole.title")}
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, mt: 1 }}
          >
            {t("organisations.detail.dialogs.assignRole.description")}
          </Typography>
          {editRoleError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {editRoleError}
            </Alert>
          )}
          <FormControl fullWidth margin="normal">
            <InputLabel>
              {t("organisations.detail.dialogs.assignRole.roleLabel")}
            </InputLabel>
            <Select
              value={editRoleValue}
              label={t("organisations.detail.dialogs.assignRole.roleLabel")}
              onChange={(e) =>
                setEditRoleValue(
                  e.target.value === "" ? "" : Number(e.target.value),
                )
              }
            >
              <MenuItem value="">
                <em>{t("organisations.detail.noRole")}</em>
              </MenuItem>
              {tenantRoles.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t("organisations.detail.dialogs.assignRole.roleHelp")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRoleOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button variant="contained" onClick={handleSaveRole}>
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
